import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

const token = localStorage.getItem('token');

let isProcessingAsroTrackTokenExchange = false;

/**
 * AsroTrack OAuth2 authorization_code flow:
 * 1. Redirect to mc_auth_url with scope from master connector (mc_scope)
 * 2. Exchange authorization code via backend POST /synctrack/asrotrack/authToken
 */
export const getAsroTrackCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    const asroTrackMaster = masterConnectors.find(
      (mc) =>
        (mc.mcRecId?.toString() === mcId?.toString() || mc.mcId?.toString() === mcId?.toString()) &&
        (mc.mcConnectorType === 'ASROTRACK' ||
          (mc.mcName && mc.mcName.toLowerCase().replace(/\s+/g, '') === 'asrotrack'))
    );

    if (!asroTrackMaster) {
      throw new Error('AsroTrack master connector not found');
    }

    const scope = asroTrackMaster.mcScope;
    if (!scope || !scope.trim()) {
      throw new Error('OAuth scope is not configured on the AsroTrack master connector (scope field)');
    }

    const authEndpoint = asroTrackMaster.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('AsroTrack authorization URL is missing from master connector data');
    }

    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }

    const connectorParams = await paramResponse.json();
    const clientId = connectorParams.find((p) =>
      ['clientid', 'client_id'].includes((p.scpName || '').toLowerCase())
    )?.scpValue;

    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
    if (!redirectUri) {
      throw new Error('OAuth redirect URI is not configured (REACT_APP_OAUTH_REDIRECT_URI)');
    }

    localStorage.setItem('PendingAsroTrackConnectorId', connectorId);
    localStorage.setItem('ConnectorType', 'AsroTrack');

    const asroTrackAuthUrl =
      `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}` +
      `response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&state=${encodeURIComponent(connectorId)}` +
      `&scope=${encodeURIComponent(scope.trim())}`;

    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      const tokenResponse = await exchangeAsroTrackAuthCodeForToken(storedAuthCode, connectorId);
      if (tokenResponse == null) {
        return null;
      }
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingAsroTrackConnectorId');
      localStorage.removeItem('ConnectorType');
      return tokenResponse;
    }

    window.location.href = asroTrackAuthUrl;
    return null;
  } catch (error) {
    console.error('AsroTrack: Error getting credentials:', error);
    throw error;
  }
};

export const exchangeAsroTrackAuthCodeForToken = async (authCode, ctdId) => {
  if (isProcessingAsroTrackTokenExchange) {
    console.warn('AsroTrack token exchange already in progress; skipping duplicate');
    return null;
  }

  isProcessingAsroTrackTokenExchange = true;

  try {
    const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;
    if (!redirectUri) {
      throw new Error('OAuth redirect URI is not configured (REACT_APP_OAUTH_REDIRECT_URI)');
    }

    const pendingConnectorId = localStorage.getItem('PendingAsroTrackConnectorId') || ctdId;
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/asrotrack/authToken`;

    const response = await fetch(
      `${tokenUrl}?ctdId=${encodeURIComponent(ctdId)}&code=${encodeURIComponent(authCode)}&redirectUri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(pendingConnectorId)}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to exchange authorization code for token: ${response.status} - ${errorText}`);
    }

    const tokenData = await response.json();
    if (tokenData.error) {
      throw new Error(tokenData.message || tokenData.error || 'Failed to get AsroTrack token');
    }

    const expiresIn = parseInt(tokenData.expires_in, 10);

    return {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      token_type: tokenData.token_type || 'Bearer',
      expires_in: expiresIn,
      status: 'Connected',
    };
  } catch (error) {
    console.error('AsroTrack token exchange error:', error);
    throw error;
  } finally {
    isProcessingAsroTrackTokenExchange = false;
  }
};

export default {
  getAsroTrackCredentialsAndCreateToken,
  exchangeAsroTrackAuthCodeForToken,
};
